import type { FieldValues } from 'react-hook-form';
import type {
  Limits,
  LostProfitInfo,
  MerchantUserFeeResponce,
  Notification,
  PayGroupCategories,
  PayHubAdminPanelDomainMethodsEnumsPayMethodType,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType,
  PayHubCashierContractsCashierSettingsResponse,
  PayHubCashierContractsLastDeclineInfoResponse,
  PayHubCashierContractsLostProfitMerchantBonus,
  PayHubCashierContractsPaymentGroupInfo as GroupInfo,
  PayHubCashierContractsPaymentsInitPaymentGroupsCurrencyResponse as Currency,
  PayHubCashierContractsPaymentsInitPaymentGroupsResponse as InitSuccess,
  PayHubCashierContractsTaxResponse,
  PayHubCashierContractsTrustedContactResponse,
  PayHubCashierContractsViewDataResponse,
  PayHubCashierContractsWithdrawalReverseResponse,
  PayoutGroupCategories,
  UserBonusInfo,
} from 'store/models';
import {
  PayHubDomainCashierEnumsCashierViewDataStyle,
  PayHubDomainCashierEnumsCashierViewDataType,
  RenderCategory,
} from 'store/models';
import type { CurrencyBase } from 'store/slices/wallet';
import { isMethodAvailable } from 'utils/getIsMethodActive';

import { transformLastDeclineInfoToString } from '../../Modulor/Withdrawal/utils';
import { multiply, subtract, sum, toBig } from '../../utils/math/math';

export interface Tier {
  from?: number;
  to?: number;
  percent_value?: number;
  absolute_value?: number;
}

export type GeneralInfo = {
  isLimitReached: boolean;
  generalViewData: PayHubCashierContractsViewDataResponse[] | null | undefined;
  lost_profit_merchant_bonus?: PayHubCashierContractsLostProfitMerchantBonus[] | null;
  disabledByWager?: boolean;
  userBonuses?: UserBonusInfo[] | null;
};

export type MethodsType = {
  title: string;
  isActive: boolean;
};

export interface UIPaymentGroups {
  arrangement: number;
  id: number;
  name: string;
  displayName: string;
  isPopular: boolean;
  logoUri: string;
  nativePayType: string;
  paymentType: string;
  requiredFields: unknown[];
  currencyId: number;
  currencyPrecision: number;
  currencyAlpha3?: string;
  descriptionUrl: string;
  limits: Limits;
  amountRegExpFe: string;
  viewData: unknown[];
  payGroupActionType: PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType | string;
  fee: string;
  merchantUserFee?: MerchantUserFeeResponce;
  processingTimeTranslationKey?: string;
  showProcessingTime: boolean;
  isSelfTarget: boolean;
  enabled: boolean;
  disabledByCategory: boolean;
  restrictedUntil?: string;
  pay_method_type: PayHubAdminPanelDomainMethodsEnumsPayMethodType | string;
  last_used: boolean;
  autoSubmit: boolean;
  isCheckout: boolean;
  preSelectedMethod: boolean;
  isLostProfit: boolean;

  lostProfitInfo: LostProfitInfo | null;
  isLoading: boolean;
  showInCarousel: boolean;
  isIncludeBonus: boolean;
  walletConnect?: boolean;
  linkedWalletConnectGroup?: number;
  amountCanBeChanged?: boolean;
  possibleChangesAmount?: number;
}

export type UIPayoutGroups = Omit<
  UIPaymentGroups,
  'lostProfitInfo' | 'preSelectedMethod' | 'isLostProfit' | 'isIncludeBonus'
>;

export type DisabledMethodsGroups = {
  disabledMethodListUntil?: UIPaymentGroups[][] | UIPayoutGroups[][];
  disabledMethodList?: UIPaymentGroups[][] | UIPayoutGroups[][];
};

export type VerificationData = {
  trustedContacts?: PayHubCashierContractsTrustedContactResponse[];
  supportUrl?: string;
  fields?: FieldValues;
};
export interface UIPayment {
  methodsTypes: MethodsType[];
  isReady: boolean;
  canAutoRedirect: boolean;
  isRequestPending: boolean;
  submitPaymentReady: boolean;
  currencyId?: number;
  currencyAlpha3?: string;
  methodToRedirect: UIPaymentGroups | undefined;
  widgetSettings: Pick<InitSuccess, 'cashier_settings'>['cashier_settings'];
  growthbookFeatures?: string[];
  methods: UIPaymentGroups[];
  carouselMethodList: UIPaymentGroups[];
  viewData: PayHubCashierContractsViewDataResponse[];
  userBalance: number;
  tax?: PayHubCashierContractsTaxResponse;
  withdrawalReverse?: PayHubCashierContractsWithdrawalReverseResponse;
  merchantUrl?: string;
  merchantSuccessUrl?: string;
  merchantFailUrl?: string;
  merchantProcessingUrl?: string;
  merchantRedirectType?: string;
  authToken: string;
  merchantApiKey: string;
  userBonuses: UserBonusInfo[] | null;
  paygroupCategories?: PayGroupCategories;
  shouldSkipInitFeature?: boolean;
  walletCurrencies: CurrencyBase[];
  selectedCurrency: CurrencyBase | null;
  userCurrency: string | null;
  userBlocked: boolean;
  defaultFiatCurrency: string | null;
  meshMethods: UIPaymentGroups[];
}

type AdditionalCardsData = {
  card_id?: number;
};

export type UIWithdrawalCard = {
  additional_data?: AdditionalCardsData;
  label?: string;
  value?: string;
  isNew?: boolean;
};

export interface UIPayout {
  methodsTypes: MethodsType[];
  isReady: boolean;
  canAutoRedirect: boolean;
  isRequestPending: boolean;
  submitPaymentReady: boolean;
  currencyId?: number;
  currencyAlpha3?: string;
  widgetSettings: PayHubCashierContractsCashierSettingsResponse;
  lastDeclineInfo?: PayHubCashierContractsLastDeclineInfoResponse | null;
  methods: UIPayoutGroups[];
  carouselMethodList: UIPayoutGroups[];
  methodToRedirect?: UIPayoutGroups;
  viewData: PayHubCashierContractsViewDataResponse[];
  userBalance: number;
  tax?: PayHubCashierContractsTaxResponse;
  cards?: UIWithdrawalCard[];
  selectedCard?: UIWithdrawalCard;
  accountVerifBlocked?: boolean;
  accountVerifBlockedUntil?: string | null;
  verifyNewPayoutAccount?: boolean;
  verificationData?: VerificationData;
  merchantUrl?: string;
  merchantSuccessUrl?: string;
  merchantFailUrl?: string;
  merchantProcessingUrl?: string;
  merchantRedirectType?: string;
  growthbookFeatures?: string[];
  userAmsSegments?: string[];
  authToken: string;
  merchantApiKey: string;
  paygroupCategories?: PayoutGroupCategories;
  shouldSkipInitFeature?: boolean;
  walletCurrencies: CurrencyBase[];
  selectedCurrency: CurrencyBase | null;
  userCurrency: string | null;
  wagerFeePercent?: number;
  wagerAmount?: number;
  userBlocked: boolean;
  defaultFiatCurrency: string | null;
  meshMethods: UIPayoutGroups[];
}

export type ViewDataType = PayHubCashierContractsViewDataResponse[] | null | undefined;

const getNotificationByPartialMatch = (
  searchValue: string,
  viewData: ViewDataType,
): PayHubCashierContractsViewDataResponse | null => {
  if (!Array.isArray(viewData)) {
    return null;
  }

  const limitNotification = viewData.find(({ key }) => key && key.includes(searchValue));

  if (limitNotification) {
    return limitNotification;
  }

  return null;
};

export const getIsLimitReached = (viewData: ViewDataType) => {
  const limitBlockNotification = getNotificationByPartialMatch('limit', viewData);

  if (!limitBlockNotification) {
    return false;
  }

  return !limitBlockNotification.params?.amount;
};

export const getEnabled = (
  isLimitReached: boolean,
  min: number,
  method: GroupInfo,
  viewData: ViewDataType,
  disabledByWager = false,
) => {
  if (isLimitReached) {
    return false;
  }

  const limitBlockNotification = getNotificationByPartialMatch('limit', viewData);

  if (typeof min === 'number' && min > limitBlockNotification?.params?.amount) {
    return false;
  }

  if (disabledByWager) {
    return false;
  }

  return method.enabled === undefined ? true : method.enabled;
};

export const sortByArrangement = <T extends { arrangement?: number }>(a: T, b: T): number => {
  if (!a || !b || typeof a.arrangement !== 'number' || typeof b.arrangement !== 'number') {
    return 0;
  }

  return a.arrangement > b.arrangement ? -1 : 1;
};

interface FormNotificationDataProps {
  viewData: ViewDataType;
  withdrawalReverse?: PayHubCashierContractsWithdrawalReverseResponse;
  isGeneral?: boolean;
  payGroupId?: number;
  lastDeclineInfo?: PayHubCashierContractsLastDeclineInfoResponse | null;
}
export const formNotificationData = (props: FormNotificationDataProps) => {
  const { viewData, isGeneral, payGroupId, lastDeclineInfo } = props;
  const generalCategories = [RenderCategory.depositMethodList, RenderCategory.withdrawalMethodList];
  const formCategories = [RenderCategory.depositForm, RenderCategory.withdrawalForm];
  const categories = isGeneral ? generalCategories : formCategories;
  let lastDeclineInfoBlock: Notification | null = null;

  if (lastDeclineInfo) {
    const result = transformLastDeclineInfoToString(lastDeclineInfo);

    if (result.declineSubReason && result.declineSubReason.trim() !== '') {
      lastDeclineInfoBlock = {
        key: 'decline',
        value: result.declineSubReason,
        extraValue: result.declineComment,
        categories,
        payGroupId,
        style: PayHubDomainCashierEnumsCashierViewDataStyle.Warning,
        type: PayHubDomainCashierEnumsCashierViewDataType.InformationBlock,
        removed: false, // нужно определить дефолтное значение, если это обязательное поле
      };
    }
  }

  const viewDataNotifications = Array.isArray(viewData)
    ? viewData.map((notification) => ({
        ...notification,
        key:
          notification.key ||
          JSON.stringify({
            ...notification,
            categories: `${categories}`,
            payGroupId,
          }),
        categories,
        payGroupId,
      }))
    : [];

  return lastDeclineInfoBlock ? [lastDeclineInfoBlock, ...viewDataNotifications] : viewDataNotifications;
};

export const getCurrency = (pmMethod: GroupInfo) => {
  if (Array.isArray(pmMethod.currencies) && pmMethod.currencies[0]) {
    return pmMethod.currencies[0];
  }

  return {
    currency: {
      currency_id: 0,
      name: '',
      alpha3: '',
      iso_number: '',
      precision: 0,
    },
  };
};

interface GetTax {
  tax?: PayHubCashierContractsTaxResponse;
  amount?: number;
}

type CalculateAmountWithTaxes = {
  amount: number;
  nonTaxableAmount?: number;
  taxes?: number | Tier;
  tax?: PayHubCashierContractsTaxResponse;
};

export interface CalculateAmountWithTaxesResponse {
  calculatedAmount: number;
  taxAmount: number;
}

export const getNonTaxableAmount = (props: PayHubCashierContractsTaxResponse): number => {
  const tier = props.tiers?.find(({ percent_value, absolute_value }) => percent_value === 0 && absolute_value === 0);

  return tier?.to || 0;
};

export const getTax = (props?: GetTax): Tier | undefined => {
  const { tax, amount } = props || {};

  if (tax?.tiers && amount) {
    return tax.tiers.find(({ from = 0, to = 0 }) => from <= amount && amount <= to);
  }

  return undefined;
};

/*
 * Node: calculateAmountWithTaxes logic
 * taxes as number - simple calculation. Tax - already in correct form like 0.05 or 0.025
 *
 * taxes as object - calculation ин levels/tiers/ladder. Every tier should be calculated.
 * For example:
 * There are several tiers: [
 *       {from: 0, to: 199, %: 5},
 *       {from: 200, to: 499, %: 15},
 *       {from: 500, to: 10000, %25}
 * ]
 * In case amount = 300:
 *   1. calculate tax amount for first tier, for tiers[0].to
 *   2. calculate tax for second tier, for (amount - for tiers[0].from)
 *   3. Do not calculate tax for third tier, because of amount not hit in third tier.
 *   4. after put everithing together
 * */
export const calculateAmountWithTaxes = (props: CalculateAmountWithTaxes): CalculateAmountWithTaxesResponse => {
  const { amount, nonTaxableAmount = 0, taxes, tax } = props;

  if (tax && typeof taxes === 'object' && amount > nonTaxableAmount && !!tax.tiers?.length) {
    const calculatedTaxObject = tax.tiers.reduce(
      (acc, tier) => {
        const { from = 0, to = 0, percent_value = 0, absolute_value = 0 } = tier;

        if (from < amount) {
          if (amount > to) {
            const diffBig = subtract(to, from);
            const taxPartBig = multiply(diffBig, percent_value);
            const preparedTax = sum([taxPartBig, absolute_value]);

            return {
              calculatedAmount: subtract(sum([acc.calculatedAmount, diffBig]), preparedTax),
              taxAmount: sum([acc.taxAmount, preparedTax]),
            };
          }
          if (amount <= to) {
            const diffBig = subtract(amount, from);
            const taxPartBig = multiply(diffBig, percent_value);
            const preparedTax = sum([taxPartBig, absolute_value]);

            return {
              calculatedAmount: subtract(sum([acc.calculatedAmount, diffBig]), preparedTax),
              taxAmount: sum([acc.taxAmount, preparedTax]),
            };
          }
        }

        return acc;
      },
      {
        calculatedAmount: toBig(0),
        taxAmount: toBig(0),
      },
    );

    return {
      calculatedAmount: parseFloat(calculatedTaxObject.calculatedAmount.toFixed(2)),
      taxAmount: parseFloat(calculatedTaxObject.taxAmount.toFixed(2)),
    };
  }

  return {
    calculatedAmount: amount,
    taxAmount: 0,
  };
};

interface GetAmountWithTaxes {
  isExtraTaxInfoShown: boolean;
  tax: PayHubCashierContractsTaxResponse;
  amount: number;
  calculateNonTaxableAmount: number;
  taxValue?: Tier;
}

export const getAmountWithTaxes = (props: GetAmountWithTaxes): CalculateAmountWithTaxesResponse => {
  const { isExtraTaxInfoShown, tax, amount, calculateNonTaxableAmount, taxValue } = props;

  if (isExtraTaxInfoShown && tax) {
    return calculateAmountWithTaxes({
      amount,
      nonTaxableAmount: calculateNonTaxableAmount,
      taxes: taxValue,
      tax,
    });
  }
  return {
    calculatedAmount: Number(amount),
    taxAmount: 0,
  };
};

export const getMinLimit = (currentCurrency: Currency): number => {
  try {
    if (typeof currentCurrency.limits!.min_amount === 'number') {
      return currentCurrency.limits!.min_amount;
    }
    return 0;
  } catch (error) {
    return 0;
  }
};

export const getMaxAmount = (currentCurrency: Currency): number | undefined => {
  try {
    if (typeof currentCurrency.limits!.max_amount === 'number') {
      return currentCurrency.limits!.max_amount;
    }
  } catch (error) {
    return undefined;
  }
};

const methodsPaymentTranslationKeys: Record<string, { translationKey: string; sort: number }> = {
  popular: {
    translationKey: 'CASHIER.POPULAR',
    sort: 0,
  },
  all: {
    translationKey: 'CASHIER.ALL',
    sort: 1,
  },
  'bank transfers': {
    translationKey: 'CASHIER.BANK_TRANSFER',
    sort: 2,
  },
  'credit and debit cards': {
    translationKey: 'CASHIER.CREDIT_DEBIT_CARD',
    sort: 3,
  },
  'e-wallets': {
    translationKey: 'CASHIER.E_WALLETS',
    sort: 4,
  },
  'prepaid & cash': {
    translationKey: 'CASHIER.CASH',
    sort: 5,
  },
  crypto: {
    translationKey: 'CASHIER.CRYPTO',
    sort: 6,
  },
  terminal: {
    translationKey: 'CASHIER.TERMINAL',
    sort: 7,
  },
  mobile: {
    translationKey: 'CASHIER.MOBILE',
    sort: 8,
  },
  'bet shops': {
    translationKey: 'CASHIER.BET_SHOPS',
    sort: 9,
  },
  'other types': {
    translationKey: 'CASHIER.OTHER',
    sort: 10,
  },
};

export const getMethodsTypes = (
  methods: Pick<UIPaymentGroups, 'nativePayType' | 'pay_method_type'>[],
): MethodsType[] => {
  const uniqMethods = [
    ...new Set(
      methods
        .map((method) => method.pay_method_type && method.pay_method_type.toLowerCase())
        .filter((method) => method !== ''),
    ),
  ];
  if (uniqMethods.length > 0) {
    const sortedMethods = uniqMethods.sort((curr: string, next: string) => {
      const currentMethod = methodsPaymentTranslationKeys[curr];
      const nextMethod = methodsPaymentTranslationKeys[next];
      if (currentMethod && nextMethod) {
        return currentMethod.sort - nextMethod.sort;
      }
      return -1;
    });

    return sortedMethods.map((method) => ({
      title: method,
      isActive: false,
    }));
  }
  return [];
};

export enum ExpirationDateKeys {
  CARD_EXP_Month = 'card_exp_month',
  CARD_EXP_Year = 'card_exp_year',
  Month = 'month',
  Year = 'year',
}

export enum FlowForPrepareExpDate {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  CHECKOUT = 'checkout',
}

export const getExpirationObject = (
  values: string,
  fieldExpDateYearName: string,
  fieldExpDateMonthName: string,
  flowType: FlowForPrepareExpDate.DEPOSIT | FlowForPrepareExpDate.WITHDRAWAL | FlowForPrepareExpDate.CHECKOUT,
) => {
  try {
    const [month, year] = values.split('/');

    const yearNameFieldMapper = {
      [FlowForPrepareExpDate.WITHDRAWAL]: fieldExpDateYearName,
      [FlowForPrepareExpDate.CHECKOUT]: ExpirationDateKeys.CARD_EXP_Year,
      [FlowForPrepareExpDate.DEPOSIT]: ExpirationDateKeys.Year,
    };

    const monthNameFieldMapper = {
      [FlowForPrepareExpDate.WITHDRAWAL]: fieldExpDateMonthName,
      [FlowForPrepareExpDate.CHECKOUT]: ExpirationDateKeys.CARD_EXP_Month,
      [FlowForPrepareExpDate.DEPOSIT]: ExpirationDateKeys.Month,
    };

    return {
      [yearNameFieldMapper[flowType]]: year,
      [monthNameFieldMapper[flowType]]: month,
    };
  } catch (e) {
    return {
      [ExpirationDateKeys.Month]: '',
      [ExpirationDateKeys.Year]: '',
    };
  }
};

const filterCarouselMethods = <T extends UIPayoutGroups | UIPaymentGroups>(methods: T[]) =>
  methods.filter((method) => method.showInCarousel && isMethodAvailable(method)).sort(sortByArrangement);

const mergeCarouselMethods = <T extends UIPayoutGroups | UIPaymentGroups>(
  filteredCarouselMethods: T[],
  methodToRedirect?: T,
) => {
  if (!methodToRedirect) {
    return filteredCarouselMethods.slice(0, 5);
  }

  const withoutRedirect = filteredCarouselMethods.filter(({ id }) => id !== methodToRedirect.id);
  return [methodToRedirect, ...withoutRedirect].slice(0, 5);
};

interface GetCarouselMethodsAndMethodToRedirect<T extends UIPayoutGroups | UIPaymentGroups> {
  methodToRedirect?: T;
  carouselMethodList: T[];
}
export const getCarouselMethodsAndMethodToRedirect = <T extends UIPayoutGroups | UIPaymentGroups>(
  methods: T[],
  getMethodForAutoRedirectCallback: (initCarouselMethods: T[]) => T | undefined,
): GetCarouselMethodsAndMethodToRedirect<T> => {
  const filteredCarouselMethods = filterCarouselMethods<T>(methods);
  const methodToRedirect = getMethodForAutoRedirectCallback(filteredCarouselMethods);
  const carouselMethodList = mergeCarouselMethods(filteredCarouselMethods, methodToRedirect);

  return {
    methodToRedirect,
    carouselMethodList,
  };
};
